import React, { useState } from 'react';
import { ListOrdered, Trash2, CheckSquare } from 'lucide-react';
import { useEditorStore, PPM } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import { useConfirmStore } from '../../store/confirmStore';
import { applyMask, buildMaskVars, MASK_PRESETS, previewMask } from '../../utils/naming';
import { api } from '../../utils/api';
import { FIELD_STYLE, ROW, LABEL, SECTION } from './PropertiesEditorStyles';
import { toast } from 'sonner';
import { WhatIfPanel } from './WhatIfPanel';

export const DcInspector: React.FC<{ dcCode?: string }> = ({ dcCode }) => {
  const {
    racks, updateRack, rows, updateRow, deleteRow, dcSettings, setDcSettings, setSelectedIds
  } = useEditorStore(useShallow(state => ({
    racks: state.racks, updateRack: state.updateRack, rows: state.rows, updateRow: state.updateRow, 
    deleteRow: state.deleteRow, dcSettings: state.dcSettings, setDcSettings: state.setDcSettings, 
    setSelectedIds: state.setSelectedIds
  })));

  const [usingCustomMask, setUsingCustomMask] = useState(false);
  const [renumberingDb, setRenumberingDb] = useState(false);

  const mask = dcSettings.namingMask;
  const preview = previewMask(mask, dcSettings.module, dcSettings.hall);

  const handleRenumber = () => {
    const rowValues = Object.values(rows).sort((a, b) => a.labelIndex - b.labelIndex);
    const rowByIndex: Record<string, number> = {};
    rowValues.forEach((r, i) => { rowByIndex[r.id] = i; });

    const racksByRow: Record<string, typeof racks[string][]> = {};
    Object.values(racks).forEach(rack => {
      const key = rack.rowId || '__unassigned';
      if (!racksByRow[key]) racksByRow[key] = [];
      racksByRow[key].push(rack);
    });

    Object.entries(racksByRow).forEach(([rowId, rowRacks]) => {
      rowRacks.sort((a, b) => {
        const pa = a.rackPosInRow ?? Infinity;
        const pb = b.rackPosInRow ?? Infinity;
        if (pa !== Infinity && pb !== Infinity) return pa - pb;
        return a.x - b.x;
      });

      const rowIdx = rowId !== '__unassigned' ? (rowByIndex[rowId] ?? 0) : 0;
      const rowNum = rowIdx + 1;

      rowRacks.forEach((rack, pos) => {
        const label = applyMask(mask, buildMaskVars({
          module: dcSettings.module,
          hall: dcSettings.hall,
          row: rowNum,
          rack: pos + 1,
        }));
        updateRack(rack.id, {
          label,
          rackPosInRow: pos + 1,
          rowIndex: rowIdx,
          rackIndex: pos,
          isDirty: true,
        });
      });
    });
  };

  const handleDbRenumber = async () => {
    if (!dcCode) return;
    setRenumberingDb(true);
    try {
      const res = await api.post(`/api/datacenters/${dcCode}/racks/renumber`, {});
      const updated = res.data as Array<{
        id: number; row_index: number; rack_index: number;
        x_m: number | null; y_m: number | null;
      }>;
      Object.values(racks).forEach(rackItem => {
        const match = updated.find(u =>
          Math.abs((u.x_m ?? -1) - rackItem.x / PPM) < 0.05 &&
          Math.abs((u.y_m ?? -1) - rackItem.y / PPM) < 0.05
        ) || updated.find(u =>
          u.row_index === rackItem.rowIndex && u.rack_index === rackItem.rackIndex
        );
        if (match) {
          updateRack(rackItem.id, { rowIndex: match.row_index, rackIndex: match.rack_index });
        }
      });
      toast.success('Индексы обновлены в БД');
    } catch (e: any) {
      console.error(e);
    } finally {
      setRenumberingDb(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {dcCode && <WhatIfPanel dcCode={dcCode} />}
      <div style={{ padding: '12px 14px', fontSize: 12, overflowY: 'auto' }}>
        <div style={{ fontWeight: 600, color: '#9ca3af', marginBottom: 12, fontSize: 12 }}>
          Настройки ЦОД
        </div>
        <div style={SECTION}>Схема нумерации стоек</div>
        <div style={{ ...ROW, marginBottom: 6 }}>
          <label style={LABEL}>Шаблон маски</label>
        <select
          style={{ ...FIELD_STYLE, cursor: 'pointer' }}
          value={usingCustomMask ? 'custom' : mask}
          onChange={e => {
            if (e.target.value === 'custom') {
              setUsingCustomMask(true);
            } else {
              setUsingCustomMask(false);
              setDcSettings({ namingMask: e.target.value });
            }
          }}
        >
          {MASK_PRESETS.map(p => (
            <option key={p.mask} value={p.mask}>{p.label}</option>
          ))}
          <option value="custom">Своя маска...</option>
        </select>
      </div>
      {usingCustomMask && (
        <div style={{ ...ROW, marginBottom: 6 }}>
          <label style={LABEL}>Своя маска</label>
          <input
            style={FIELD_STYLE}
            value={mask}
            onChange={e => setDcSettings({ namingMask: e.target.value })}
            placeholder="{module}-{row:02d}-{rack:02d}"
          />
        </div>
      )}
      <div style={{ ...ROW, marginBottom: 8 }}>
        <label style={LABEL}>Переменные маски</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
          <input style={FIELD_STYLE} value={dcSettings.module}
            onChange={e => setDcSettings({ module: e.target.value })}
            placeholder="Модуль: 01" />
          <input style={FIELD_STYLE} value={dcSettings.hall}
            onChange={e => setDcSettings({ hall: e.target.value })}
            placeholder="Зал: A" />
        </div>
        <div style={{ fontSize: 10, color: '#4b5563', marginTop: 3 }}>
          Пример: <span style={{ color: '#60a5fa', fontFamily: 'monospace' }}>{preview}</span>
        </div>
      </div>
      <button onClick={handleRenumber} style={{
        width: '100%', padding: '6px 8px', marginBottom: 6,
        background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
        borderRadius: 4, color: '#60a5fa', fontSize: 11, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}>
        <ListOrdered size={13} /> Перенумеровать (черновик)
      </button>
      {dcCode && (
        <button
          onClick={handleDbRenumber}
          disabled={renumberingDb}
          style={{
            width: '100%', padding: '5px', marginBottom: 4,
            background: renumberingDb ? '#1f2937' : 'rgba(16,185,129,0.1)',
            border: `1px solid ${renumberingDb ? '#374151' : 'rgba(16,185,129,0.3)'}`,
            borderRadius: 4, color: renumberingDb ? '#4b5563' : '#34d399',
            fontSize: 11, cursor: renumberingDb ? 'not-allowed' : 'pointer',
          }}
        >
          {renumberingDb ? 'Перенумерация...' : 'Перенумеровать в БД'}
        </button>
      )}
      <div style={{ height: 1, background: '#1f2937', marginBottom: 12 }} />
      <div style={SECTION}>Размеры по умолчанию</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={ROW}>
          <label style={LABEL}>Ширина (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.05} value={dcSettings.defaultRackWidthM}
            onChange={e => setDcSettings({ defaultRackWidthM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Глубина (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.05} value={dcSettings.defaultRackDepthM}
            onChange={e => setDcSettings({ defaultRackDepthM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Высота (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.1} value={dcSettings.defaultRackHeightM}
            onChange={e => setDcSettings({ defaultRackHeightM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Лоток над стойкой (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.05} value={dcSettings.defaultTrayHeightM}
            onChange={e => setDcSettings({ defaultTrayHeightM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Ширина коридора (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.1} value={dcSettings.defaultAisleWidthM}
            onChange={e => setDcSettings({ defaultAisleWidthM: parseFloat(e.target.value) })} />
        </div>
      </div>
      <div style={{ height: 1, background: '#1f2937', marginBottom: 12 }} />
      <div style={SECTION}>Ряды</div>
      {Object.values(rows).length === 0 && (
        <div style={{ fontSize: 11, color: '#4b5563', fontStyle: 'italic', marginBottom: 8 }}>
          Нет рядов. Создайте через Матричный генератор.
        </div>
      )}
      {Object.values(rows).sort((a, b) => a.labelIndex - b.labelIndex).map(row => {
        const rackCount = Object.values(racks).filter(r => r.rowId === row.id).length;
        return (
          <div key={row.id} style={{
            background: '#111827', border: '1px solid #1f2937', borderRadius: 4,
            padding: '6px 8px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: row.color, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                style={{ ...FIELD_STYLE, padding: '1px 4px', width: '100%' }}
                value={row.name}
                onChange={e => updateRow(row.id, { name: e.target.value })}
              />
            </div>
            <div style={{ fontSize: 10, color: '#4b5563', flexShrink: 0 }}>{rackCount} ст.</div>
            <button
              onClick={() => setSelectedIds(Object.values(racks).filter(r => r.rowId === row.id).map(r => r.id))}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
              title="Выделить все стойки ряда"
            ><CheckSquare size={12} /></button>
            <button
              onClick={async () => {
                const confirmed = await useConfirmStore.getState().confirm({
                  title: 'Удаление ряда стоек',
                  message: `Удалить ряд "${row.name}"?`,
                  confirmText: 'Удалить',
                  cancelText: 'Отмена'
                });
                if (confirmed) deleteRow(row.id);
              }}
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
              title="Удалить ряд"
            ><Trash2 size={12} /></button>
          </div>
        );
      })}
      </div>
    </div>
  );
};
